import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { medalTableService } from '../../services/medalTableService';

const REGION_I18N_KEY = {
  Europe: "europe",
  Asia: "asia",
  America: "america",
  Oceania: "oceania",
  Africa: "africa",
};

const MedalTableTailwind = () => {
  const { t, i18n } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRegion, setSelectedRegion] = useState("all");
  const [sortConfig, setSortConfig] = useState({ key: "rank", direction: "asc" });
  const [medalData, setMedalData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAllNations, setShowAllNations] = useState(false);

  useEffect(() => {
    loadMedalData();
  }, []);

  const loadMedalData = useCallback(async () => {
    try {
      setLoading(true);
      const data = await medalTableService.getMedalTable();
      setMedalData(data && data.length > 0 ? data : []);
    } catch (error) {
      console.error('Fehler beim Laden des Medaillenspiegels:', error);
      setMedalData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const regionOptions = [
    { value: "all", label: t("admin.medalTable.allRegions") },
    { value: "Europe", label: t("admin.medalTable.europe") },
    { value: "Asia", label: t("admin.medalTable.asia") },
    { value: "America", label: t("admin.medalTable.america") },
    { value: "Oceania", label: t("admin.medalTable.oceania") },
    { value: "Africa", label: t("admin.medalTable.africa") },
  ];

  const getRegionDisplayName = (nation) => {
    const overrideKey = `admin.medalTable.nationRegionOverride.${nation.nation}`;
    if (i18n.exists(overrideKey)) return t(overrideKey);
    const rk = REGION_I18N_KEY[nation.region];
    if (rk) return t(`admin.medalTable.${rk}`);
    return nation.region || "—";
  };

  const filteredMedals = medalData.filter((item) => {
    const matchesSearch = item.nation.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRegion = selectedRegion === "all" || item.region === selectedRegion;
    return matchesSearch && matchesRegion;
  });

  const sortKeys = {
    rank: (a, b) => a.rank - b.rank,
    nation: (a, b) => a.nation.localeCompare(b.nation),
    gold: (a, b) => b.gold - a.gold,
    silver: (a, b) => b.silver - a.silver,
    bronze: (a, b) => b.bronze - a.bronze,
    total: (a, b) => b.total - a.total
  };

  const sortedMedals = [...filteredMedals].sort((a, b) => {
    if (sortConfig.key === "rank") {
      if (a.gold !== b.gold) return b.gold - a.gold;
      if (a.silver !== b.silver) return b.silver - a.silver;
      if (a.bronze !== b.bronze) return b.bronze - a.bronze;
      return b.total - a.total;
    }
    const cmp = sortKeys[sortConfig.key](a, b);
    return sortConfig.direction === "asc" ? cmp : -cmp;
  });

  const medalsWithRanks = sortedMedals.map((nation, index) => ({
    ...nation,
    rank: index + 1
  }));

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
      }
      return { key, direction: "asc" };
    });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return (
        <i
          className="fas fa-sort ml-1 text-[10px] text-slate-300 dark:text-slate-600"
          aria-hidden
        />
      );
    }
    return sortConfig.direction === "asc" ? (
      <i
        className="fas fa-sort-up relative top-[1px] ml-1 text-[10px] text-primary dark:text-primary-light"
        aria-hidden
      />
    ) : (
      <i
        className="fas fa-sort-down relative -top-[1px] ml-1 text-[10px] text-primary dark:text-primary-light"
        aria-hidden
      />
    );
  };

  const getTotalMedals = () => medalsWithRanks.reduce((t, n) => t + (n.total || 0), 0);
  const getTotalGold = () => medalsWithRanks.reduce((t, n) => t + (n.gold || 0), 0);
  const getTotalSilver = () => medalsWithRanks.reduce((t, n) => t + (n.silver || 0), 0);
  const getTotalBronze = () => medalsWithRanks.reduce((t, n) => t + (n.bronze || 0), 0);

  const handleResetFilters = () => {
    setSearchTerm("");
    setSelectedRegion("all");
  };

  const getActiveFilters = () => {
    const active = [];
    if (selectedRegion !== "all") {
      const label = regionOptions.find((r) => r.value === selectedRegion)?.label || selectedRegion;
      active.push({
        kind: "region",
        value: label,
        onRemove: () => setSelectedRegion("all"),
      });
    }
    if (searchTerm.trim() !== "") {
      active.push({
        kind: "search",
        value: searchTerm,
        onRemove: () => setSearchTerm(""),
      });
    }
    return active;
  };

  const top3Nations = medalsWithRanks.slice(0, 3);
  const tableNations = showAllNations ? medalsWithRanks.slice(3) : medalsWithRanks.slice(3, 8);
  const activeFilters = getActiveFilters();
  const hasActiveFilters = activeFilters.length > 0;

  // ─── Skeleton ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <main className="pt-24 pb-8 bg-slate-50/50 dark:bg-slate-900 min-h-screen transition-colors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Header Skeleton */}
          <div className="mb-8 animate-pulse">
            <div className="flex items-center gap-3 mb-2">
              <div className="h-9 bg-slate-200 dark:bg-slate-700 rounded w-64"></div>
              <div className="h-6 w-10 bg-slate-200 dark:bg-slate-700 rounded-full"></div>
            </div>
            <div className="h-4 w-80 bg-slate-200 dark:bg-slate-700 rounded"></div>
          </div>

          {/* Filter Section Skeleton */}
          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm p-5 mb-6 animate-pulse">
            <div className="h-10 bg-slate-200 dark:bg-slate-700 rounded-xl mb-5"></div>
            <div className="h-3 w-16 bg-slate-200 dark:bg-slate-700 rounded mb-3"></div>
            <div className="flex flex-wrap gap-1.5">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-7 w-24 bg-slate-200 dark:bg-slate-700 rounded-full"></div>
              ))}
            </div>
          </div>

          {/* KPI Cards Skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8 animate-pulse">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm p-5 flex items-center gap-4">
                <div className="w-10 h-10 rounded-xl bg-slate-200 dark:bg-slate-700 flex-shrink-0"></div>
                <div className="flex-1">
                  <div className="h-3 w-24 bg-slate-200 dark:bg-slate-700 rounded mb-2"></div>
                  <div className="h-7 w-16 bg-slate-200 dark:bg-slate-700 rounded"></div>
                </div>
              </div>
            ))}
          </div>

          {/* Podium Skeleton */}
          <div className="mb-8 animate-pulse">
            <div className="h-3 w-24 bg-slate-200 dark:bg-slate-700 rounded mb-4"></div>
            <div className="grid grid-cols-1 gap-5 md:grid-cols-3 md:items-end md:gap-4">
              <div className="order-2 md:order-1">
                <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 overflow-hidden">
                  <div className="h-28 bg-slate-200 dark:bg-slate-700"></div>
                  <div className="p-4">
                    <div className="h-5 w-24 bg-slate-200 dark:bg-slate-700 rounded mb-2"></div>
                    <div className="h-3 w-16 bg-slate-200 dark:bg-slate-700 rounded mb-3"></div>
                    <div className="flex gap-2">
                      {[...Array(3)].map((_, j) => (
                        <div key={j} className="flex-1 h-8 bg-slate-200 dark:bg-slate-700 rounded"></div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              <div className="order-1 md:order-2">
                <div className="bg-white dark:bg-slate-800 rounded-2xl border-2 border-yellow-300 dark:border-yellow-600 overflow-hidden md:-translate-y-4">
                  <div className="h-36 bg-slate-200 dark:bg-slate-700"></div>
                  <div className="p-5">
                    <div className="h-6 w-28 bg-slate-200 dark:bg-slate-700 rounded mb-2"></div>
                    <div className="h-3 w-20 bg-slate-200 dark:bg-slate-700 rounded mb-4"></div>
                    <div className="flex gap-3">
                      {[...Array(3)].map((_, j) => (
                        <div key={j} className="flex-1 h-10 bg-slate-200 dark:bg-slate-700 rounded"></div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              <div className="order-3 md:order-3">
                <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 overflow-hidden">
                  <div className="h-28 bg-slate-200 dark:bg-slate-700"></div>
                  <div className="p-4">
                    <div className="h-5 w-24 bg-slate-200 dark:bg-slate-700 rounded mb-2"></div>
                    <div className="h-3 w-16 bg-slate-200 dark:bg-slate-700 rounded mb-3"></div>
                    <div className="flex gap-2">
                      {[...Array(3)].map((_, j) => (
                        <div key={j} className="flex-1 h-8 bg-slate-200 dark:bg-slate-700 rounded"></div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Table Skeleton */}
          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden animate-pulse">
            <div className="px-5 py-4 border-b border-slate-100 dark:border-slate-700">
              <div className="h-4 w-32 bg-slate-200 dark:bg-slate-700 rounded"></div>
            </div>
            <div className="divide-y divide-slate-100 dark:divide-slate-700/60">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="flex items-center gap-4 px-5 py-3.5">
                  <div className="w-8 h-6 bg-slate-200 dark:bg-slate-700 rounded"></div>
                  <div className="w-8 h-8 bg-slate-200 dark:bg-slate-700 rounded-full flex-shrink-0"></div>
                  <div className="flex-1 h-4 bg-slate-200 dark:bg-slate-700 rounded"></div>
                  <div className="w-12 h-6 bg-slate-200 dark:bg-slate-700 rounded"></div>
                  <div className="w-12 h-6 bg-slate-200 dark:bg-slate-700 rounded"></div>
                  <div className="w-12 h-6 bg-slate-200 dark:bg-slate-700 rounded"></div>
                  <div className="w-14 h-6 bg-slate-200 dark:bg-slate-700 rounded"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    );
  }

  // ─── Podium Card Renderer ─────────────────────────────────────────────────
  const renderPodiumCard = (rank) => {
    const nation = top3Nations.find(n => n.rank === rank);
    if (!nation) return <div />;

    const isFirst = rank === 1;
    const configs = {
      1: {
        border: "border-2 border-yellow-400 dark:border-yellow-500",
        leftAccent: "border-l-4 border-l-amber-500 dark:border-l-amber-400",
        shadow: "shadow-lg",
        translateY: "md:-translate-y-4",
        imgSize: "w-20 h-20",
        headerH: "h-36",
        padding: "p-5",
        nameSize: "text-xl font-bold",
        regionSize: "text-sm",
        medalSize: "text-2xl font-bold",
        labelSize: "text-[10px]",
        badgeSize: "w-7 h-7",
        badgeText: "text-sm",
        badgeColor: "border-yellow-400 dark:border-yellow-500 text-yellow-600 dark:text-yellow-400",
      },
      2: {
        border: "border border-slate-200 dark:border-slate-700",
        leftAccent: "border-l-4 border-l-slate-400 dark:border-l-slate-300",
        shadow: "shadow-sm",
        translateY: "",
        imgSize: "w-14 h-14",
        headerH: "h-28",
        padding: "p-4",
        nameSize: "text-base font-bold",
        regionSize: "text-xs",
        medalSize: "text-lg font-bold",
        labelSize: "text-[9px]",
        badgeSize: "w-6 h-6",
        badgeText: "text-xs",
        badgeColor: "border-slate-300 dark:border-slate-500 text-slate-600 dark:text-slate-300",
      },
      3: {
        border: "border border-slate-200 dark:border-slate-700",
        leftAccent: "border-l-4 border-l-orange-600 dark:border-l-orange-500",
        shadow: "shadow-sm",
        translateY: "",
        imgSize: "w-14 h-14",
        headerH: "h-28",
        padding: "p-4",
        nameSize: "text-base font-bold",
        regionSize: "text-xs",
        medalSize: "text-lg font-bold",
        labelSize: "text-[9px]",
        badgeSize: "w-6 h-6",
        badgeText: "text-xs",
        badgeColor: "border-orange-400 dark:border-orange-500 text-orange-600 dark:text-orange-400",
      },
    };
    const c = configs[rank];

    return (
      <div
        className={`overflow-hidden rounded-2xl bg-white ring-1 ring-slate-200/60 dark:bg-slate-800 dark:ring-slate-600/50 ${c.border} ${c.leftAccent} ${c.shadow} ${c.translateY}`}
      >
        {/* Flag area */}
        <div className={`relative ${c.headerH} bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-700/50 dark:to-slate-800 flex items-center justify-center`}>
          <div className="relative">
            <img
              src={process.env.PUBLIC_URL + nation.flag}
              alt={nation.nation}
              className={`${c.imgSize} object-cover rounded-full border-2 border-white dark:border-slate-600 shadow-md`}
            />
            <div className={`absolute -bottom-0.5 -right-0.5 ${c.badgeSize} flex items-center justify-center bg-white dark:bg-slate-800 rounded-full shadow-sm border ${c.badgeColor} z-10`}>
              <span className={`${c.badgeText} font-bold`}>{rank}</span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className={c.padding}>
          <h3
            className={`${c.nameSize} mb-0.5 text-pretty text-slate-900 dark:text-slate-100 break-words md:truncate`}
          >
            {nation.nation}
          </h3>
          <p className={`${c.regionSize} text-slate-500 dark:text-slate-400 mb-3`}>
            {getRegionDisplayName(nation)}
          </p>

          <div className="grid grid-cols-3 gap-2 sm:gap-1.5">
            {[
              { labelKey: "admin.medalTable.podiumGold", val: nation.gold, dot: "bg-amber-400" },
              { labelKey: "admin.medalTable.podiumSilver", val: nation.silver, dot: "bg-slate-400" },
              { labelKey: "admin.medalTable.podiumBronze", val: nation.bronze, dot: "bg-orange-400" },
            ].map(({ labelKey, val, dot }) => (
              <div
                key={labelKey}
                className="rounded-xl bg-slate-50 py-2 text-center dark:bg-slate-700/50"
              >
                <div
                  className={`${c.labelSize} mb-0.5 flex items-center justify-center gap-0.5 font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400`}
                >
                  <span className={`inline-block h-1.5 w-1.5 rounded-full ${dot}`} aria-hidden />
                  {t(labelKey)}
                </div>
                <div className={`${c.medalSize} text-slate-900 dark:text-slate-100`}>
                  {val || 0}
                </div>
              </div>
            ))}
          </div>

          {isFirst && (
            <div className="mt-3 flex items-center gap-1.5 text-xs font-medium text-emerald-600 dark:text-emerald-400">
              <i className="fas fa-arrow-trend-up text-[10px]" aria-hidden />
              <span>{t("admin.medalTable.leaderBadge")}</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <main className="pt-24 pb-8 bg-slate-50/50 dark:bg-slate-900 min-h-screen transition-colors">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* ── Header ─────────────────────────────────────────────────────────── */}
        <header className="mb-8 md:mb-10">
          <div className="flex flex-wrap items-start gap-3">
            <h1 className="text-3xl font-semibold tracking-tight text-slate-900 dark:text-slate-100 md:text-4xl">
              {t("admin.medalTable.title")}
            </h1>
            <span
              className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-300"
              aria-label={t("admin.medalTable.nationsBadgeAria", {
                count: medalsWithRanks.length,
              })}
            >
              <i className="fas fa-flag text-[10px]" aria-hidden />
              {medalsWithRanks.length}
            </span>
          </div>
          <p className="mt-2 max-w-2xl text-pretty text-sm leading-relaxed text-slate-500 dark:text-slate-400">
            {t("admin.medalTable.subtitle")}
          </p>
        </header>

        {/* ── Filter Section ─────────────────────────────────────────────────── */}
        <section className="mb-6 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm ring-1 ring-slate-200/50 dark:border-slate-700 dark:bg-slate-800 dark:ring-slate-700/50">
          <div className="relative mb-5">
            <i
              className="fas fa-search pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs text-slate-400 dark:text-slate-500"
              aria-hidden
            />
            <input
              type="search"
              autoComplete="off"
              placeholder={t("admin.medalTable.searchPlaceholder")}
              aria-label={t("admin.medalTable.searchAria")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="h-10 w-full rounded-xl border border-slate-200 bg-slate-50 pl-9 pr-4 text-sm text-slate-900 transition-colors placeholder:text-slate-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 dark:border-slate-600 dark:bg-slate-700/50 dark:text-slate-100 dark:placeholder:text-slate-500 dark:focus:border-primary-light dark:focus:ring-primary-light/20"
            />
          </div>

          <div>
            <p className="mb-2 text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400">
              {t("admin.medalTable.regionFilterLabel")}
            </p>
            <div className="flex flex-wrap gap-2" role="group" aria-label={t("admin.medalTable.regionFilterLabel")}>
              {regionOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setSelectedRegion(option.value)}
                  aria-pressed={selectedRegion === option.value}
                  className={`inline-flex h-8 items-center rounded-full px-3.5 text-xs font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/35 focus-visible:ring-offset-2 dark:focus-visible:ring-primary-light/35 dark:focus-visible:ring-offset-slate-800 ${
                    selectedRegion === option.value
                      ? "bg-primary text-white shadow-sm dark:bg-primary-light"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-700/60 dark:text-slate-300 dark:hover:bg-slate-700"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {hasActiveFilters && (
            <div className="mt-5 border-t border-slate-100 pt-4 dark:border-slate-700/60">
              <div className="mb-2.5 flex flex-wrap items-center justify-between gap-2">
                <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400">
                  {t("admin.medalTable.activeFilters")}
                </span>
                <button
                  type="button"
                  onClick={handleResetFilters}
                  className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 text-xs font-medium text-slate-500 transition-colors hover:text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-400 dark:hover:text-primary-light dark:focus-visible:ring-primary-light/30"
                >
                  <i className="fas fa-undo text-[10px]" aria-hidden />
                  {t("admin.medalTable.resetFilters")}
                </button>
              </div>
              <ul className="m-0 flex list-none flex-wrap gap-2 p-0">
                {activeFilters.map((filter, index) => (
                  <li key={index}>
                    <span className="inline-flex items-center gap-1 rounded-full border border-primary/20 bg-primary/[0.06] px-2.5 py-1 text-xs font-medium text-primary dark:border-primary-light/25 dark:bg-primary-light/10 dark:text-primary-light">
                      <span className="max-w-[12rem] truncate sm:max-w-xs">
                        {filter.kind === "region"
                          ? t("admin.medalTable.filterTypeRegion")
                          : t("admin.medalTable.filterTypeSearch")}
                        : {filter.value}
                      </span>
                      <button
                        type="button"
                        onClick={filter.onRemove}
                        className="ml-0.5 rounded p-0.5 opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                        aria-label={t("admin.medalTable.removeFilterAria")}
                      >
                        <i className="fas fa-times text-[9px]" aria-hidden />
                      </button>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>

        {/* ── KPI Cards ──────────────────────────────────────────────────────── */}
        <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5 lg:grid-cols-4">
          {[
            {
              titleKey: "admin.medalTable.kpi.goldTitle",
              value: getTotalGold(),
              icon: "🥇",
              iconBg: "bg-amber-100 dark:bg-amber-900/30",
            },
            {
              titleKey: "admin.medalTable.kpi.silverTitle",
              value: getTotalSilver(),
              icon: "🥈",
              iconBg: "bg-slate-100 dark:bg-slate-700",
            },
            {
              titleKey: "admin.medalTable.kpi.bronzeTitle",
              value: getTotalBronze(),
              icon: "🥉",
              iconBg: "bg-orange-100 dark:bg-orange-900/30",
            },
            {
              titleKey: "admin.medalTable.kpi.totalTitle",
              value: getTotalMedals(),
              iconEl: (
                <i className="fas fa-medal text-lg text-emerald-600 dark:text-emerald-400" aria-hidden />
              ),
              iconBg: "bg-emerald-100 dark:bg-emerald-900/30",
            },
          ].map(({ titleKey, value, icon, iconEl, iconBg }) => (
            <div
              key={titleKey}
              className="flex items-center justify-between rounded-2xl border border-slate-200 bg-white p-5 shadow-sm ring-1 ring-slate-200/40 dark:border-slate-700 dark:bg-slate-800 dark:ring-slate-700/40"
            >
              <div className="min-w-0 pr-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  {t(titleKey)}
                </p>
                <p className="mt-1.5 text-3xl font-bold tabular-nums text-slate-900 dark:text-slate-100">
                  {value}
                </p>
              </div>
              <div
                className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${iconBg}`}
              >
                {icon ? (
                  <span className="text-xl leading-none" aria-hidden>
                    {icon}
                  </span>
                ) : (
                  iconEl
                )}
              </div>
            </div>
          ))}
        </div>

        {/* ── Podium ─────────────────────────────────────────────────────────── */}
        {top3Nations.length > 0 && (
          <section className="mb-8" aria-labelledby="medal-podium-heading">
            <h2
              id="medal-podium-heading"
              className="mb-4 text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400"
            >
              {t("admin.medalTable.podiumTitle")}
            </h2>
            <div className="grid grid-cols-1 gap-5 md:grid-cols-3 md:items-end md:gap-4">
              <div className="order-2 min-w-0 md:order-1">{renderPodiumCard(2)}</div>
              <div className="order-1 min-w-0 md:order-2">{renderPodiumCard(1)}</div>
              <div className="order-3 min-w-0 md:order-3">{renderPodiumCard(3)}</div>
            </div>
          </section>
        )}

        {/* ── Medal Table ────────────────────────────────────────────────────── */}
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm ring-1 ring-slate-200/50 dark:border-slate-700 dark:bg-slate-800 dark:ring-slate-700/50">
          <p className="border-b border-slate-100 px-4 py-2.5 text-xs text-slate-500 dark:border-slate-700 dark:text-slate-400 md:hidden">
            {t("admin.medalTable.scrollHint")}
          </p>
          <div className="overflow-x-auto">
            <table className="w-full divide-y divide-slate-200 dark:divide-slate-700">
              <caption className="sr-only">{t("admin.medalTable.tableCaption")}</caption>
              <thead className="bg-slate-50 dark:bg-slate-700/50">
                <tr>
                  {[
                    { key: "rank", label: t("admin.medalTable.rank"), align: "left" },
                    { key: "nation", label: t("admin.medalTable.nation"), align: "left" },
                    {
                      key: "gold",
                      label: t("admin.medalTable.gold"),
                      align: "right",
                      dot: "bg-amber-400",
                    },
                    {
                      key: "silver",
                      label: t("admin.medalTable.silver"),
                      align: "right",
                      dot: "bg-slate-400",
                    },
                    {
                      key: "bronze",
                      label: t("admin.medalTable.bronze"),
                      align: "right",
                      dot: "bg-orange-400",
                    },
                    { key: "total", label: t("admin.medalTable.total"), align: "right" },
                  ].map(({ key, label, align, dot }) => {
                    const alignClass = align === "right" ? "text-right" : "text-left";
                    const ariaSort =
                      sortConfig.key === key
                        ? sortConfig.direction === "asc"
                          ? "ascending"
                          : "descending"
                        : "none";
                    return (
                      <th
                        key={key}
                        scope="col"
                        aria-sort={ariaSort}
                        onClick={() => handleSort(key)}
                        className={`cursor-pointer select-none px-4 py-3 text-xs font-medium uppercase tracking-wider transition-colors sm:px-6 ${alignClass} ${
                          sortConfig.key === key
                            ? "bg-primary/5 text-primary dark:bg-primary-light/5 dark:text-primary-light"
                            : "text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-700"
                        }`}
                      >
                        <span
                          className={`inline-flex items-center gap-1 ${align === "right" ? "w-full justify-end" : ""}`}
                        >
                          {dot ? (
                            <span
                              className={`inline-block h-1.5 w-1.5 rounded-full ${dot}`}
                              aria-hidden
                            />
                          ) : null}
                          {label}
                          {getSortIcon(key)}
                        </span>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-slate-800 divide-y divide-slate-200 dark:divide-slate-700">
                {tableNations.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center sm:px-6">
                      <div className="flex flex-col items-center gap-2 text-slate-400 dark:text-slate-500">
                        <i className="fas fa-medal text-2xl" aria-hidden />
                        <p className="max-w-sm text-pretty text-sm font-medium text-slate-500 dark:text-slate-400">
                          {t("admin.medalTable.emptyRest")}
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  tableNations.map((nation) => (
                    <tr
                      key={nation.id}
                      className="hover:bg-slate-50/50 dark:hover:bg-slate-700/50 transition-colors"
                    >
                      {/* Rang */}
                      <td className="whitespace-nowrap px-4 py-4 sm:px-6">
                        <span className="text-sm font-medium tabular-nums text-slate-900 dark:text-slate-100">
                          {nation.rank}
                        </span>
                      </td>

                      {/* Nation */}
                      <td className="whitespace-nowrap px-4 py-4 sm:px-6">
                        <div className="flex items-center gap-2.5">
                          <img
                            src={process.env.PUBLIC_URL + nation.flag}
                            alt={nation.nation}
                            className="w-6 h-6 object-cover rounded-full border border-slate-200 dark:border-slate-600 flex-shrink-0"
                          />
                          <div>
                            <span className="text-sm font-medium text-slate-900 dark:text-slate-100">
                              {nation.nation}
                            </span>
                            <span className="block text-[10px] text-slate-400 dark:text-slate-500">
                              {getRegionDisplayName(nation)}
                            </span>
                          </div>
                        </div>
                      </td>

                      {/* Gold */}
                      <td className="whitespace-nowrap px-4 py-4 text-right sm:px-6">
                        {nation.gold > 0 ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-lg bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 text-sm font-semibold">
                            {nation.gold}
                          </span>
                        ) : (
                          <span className="text-sm text-slate-400 dark:text-slate-500">—</span>
                        )}
                      </td>

                      {/* Silber */}
                      <td className="whitespace-nowrap px-4 py-4 text-right sm:px-6">
                        {nation.silver > 0 ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-lg bg-slate-100 dark:bg-slate-700/60 text-slate-700 dark:text-slate-300 text-sm font-semibold">
                            {nation.silver}
                          </span>
                        ) : (
                          <span className="text-sm text-slate-400 dark:text-slate-500">—</span>
                        )}
                      </td>

                      {/* Bronze */}
                      <td className="whitespace-nowrap px-4 py-4 text-right sm:px-6">
                        {nation.bronze > 0 ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-lg bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-400 text-sm font-semibold">
                            {nation.bronze}
                          </span>
                        ) : (
                          <span className="text-sm text-slate-400 dark:text-slate-500">—</span>
                        )}
                      </td>

                      {/* Gesamt */}
                      <td className="whitespace-nowrap px-4 py-4 text-right sm:px-6">
                        <span className="text-sm font-medium tabular-nums text-slate-900 dark:text-slate-100">
                          {nation.total ?? "—"}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Show All Button */}
          {medalsWithRanks.length > 8 && !showAllNations && (
            <div className="border-t border-slate-200 bg-slate-50/90 px-4 py-4 dark:border-slate-700 dark:bg-slate-700/25 sm:px-6">
              <button
                type="button"
                onClick={() => setShowAllNations(true)}
                className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700 dark:focus-visible:ring-primary-light/30"
              >
                <i className="fas fa-chevron-down text-xs" aria-hidden />
                {t("admin.medalTable.showMore", {
                  count: medalsWithRanks.length - 3,
                })}
              </button>
            </div>
          )}
          {showAllNations && medalsWithRanks.length > 8 && (
            <div className="border-t border-slate-200 bg-slate-50/90 px-4 py-4 dark:border-slate-700 dark:bg-slate-700/25 sm:px-6">
              <button
                type="button"
                onClick={() => setShowAllNations(false)}
                className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700 dark:focus-visible:ring-primary-light/30"
              >
                <i className="fas fa-chevron-up text-xs" aria-hidden />
                {t("admin.medalTable.showLess")}
              </button>
            </div>
          )}
        </div>

      </div>
    </main>
  );
};

export default MedalTableTailwind;
